import type { GameRoomStateView, GameSeatView, RoomPhaseView } from "@wr/game-core";
import {
  roomHasSides,
  SIDES,
  sideColourIndex,
  sideIndexForTeam,
  type RoomJoinRefusal,
  type RoomShape,
} from "@wr/contracts";
import { t, type StringKey } from "@wr/i18n";
import type { ScoreboardProps, ScoreRowView, SeatView, StandingTeamView, StatusTone } from "@wr/ui";

/**
 * One band of the between-rounds side subtotal row (S12 R2b) — **derived from
 * the component's own prop type rather than restated.**
 *
 * `ScoreboardProps.teams` is an inline anonymous shape, and Ivy §1.3 bars
 * extending the `Scoreboard` API to give it a name. Deriving it costs nothing,
 * changes no API, and makes a future edit to the prop a **compile error at
 * {@link scoreTeams}** instead of a silently-dropped field.
 */
export type ScoreTeamView = NonNullable<ScoreboardProps["teams"]>[number];

/**
 * Every derivation the room screen makes from room state — in one file, as pure
 * functions (design spec `wr-s4-room-spec.md`).
 *
 * It is separate from the screen for the reason `lib/lobby.ts` is: these are the
 * rules Quinn tests, and half of them describe states that are unreachable by
 * hand. T7 fires only when *every* seated player has dropped and every grace
 * window has expired; T2 needs an admin; T3 needs a 45-second wait in a paid
 * room. A rule that lives inside a component is a rule you can only test by
 * arranging the world.
 *
 * §19's consolidated list is the index Quinn reads. The lines that live here
 * rather than in a component are 12 (scoreboard order), 13 (winners from the
 * server), 14 (no `+0`, no negative delta), 23–28 (terminal selection and
 * precedence) and 27 (the forfeit boundary).
 */

/* ==========================================================================
   The state census (§2)
   ========================================================================== */

/**
 * **LOCKED (§2) — the map is exhaustive and Rafi may not improvise a fifth
 * live state.** Four player-facing states plus seven terminal/interrupted ones.
 */
export type RoomScreenState =
  | "waiting"
  | "round"
  | "between"
  | "results"
  | "T1"
  | "T2"
  | "T3"
  | "T4"
  | "T5"
  | "T6"
  | "T7"
  | "T8"
  /**
   * **`T9` — the socket join refusal (`SOCKET-REFUSAL-COPY`, T-698; Ivy G.7).**
   *
   * A **sibling** of `T6`, not a re-bodying of it, and Ivy ruled that the better
   * answer: *"Re-bodying would make one state mean two things (transport failure
   * AND a refusal) and destroy `T6`'s diagnostic value. `T6` must survive as the
   * honest 'nothing arrived' fallback, and `G.5.2`'s whole finding was that `T6`
   * is false about cause — a state that is false about cause cannot be repaired
   * by giving it a second cause."*
   */
  | "T9";

/* ==========================================================================
   The socket join-refusal composition map (`SOCKET-REFUSAL-COPY`, T-698)
   ========================================================================== */

/**
 * What a `4409` socket refusal composes.
 *
 * `T9` carries the sentence its **HTTP twin** already composes, by KEY — Ivy
 * `G.5.3` criterion 3: *"A socket refusal composes the same catalogued sentence
 * its HTTP twin composes… **plumbed to a second surface rather than
 * re-authored**. Its bytes do not move."* Holding the **key** rather than the
 * sentence is what makes "bytes unmoved" structural instead of a promise: this
 * file cannot restate a sentence, only point at one, and `StringKey` makes a
 * typo a compile error rather than a blank panel.
 */
export type RefusalComposition =
  | { readonly screen: "T9"; readonly titleKey: StringKey | null; readonly bodyKey: StringKey }
  /** Unchanged from today: this refusal keeps composing `T6`, tone and all. */
  | { readonly screen: "T6" };

/**
 * **The exhaustive map, and it is the row's highest-value deliverable — not the
 * copy** (Dex Add. 10 §1, A135's registration-time forced question):
 *
 * > *"The client's map is typed `Record<RoomJoinRefusal, string>` … so **a sixth
 * > refusal added on the server DOES NOT COMPILE until someone writes its
 * > message.** That converts 'the client silently shows T6 for a new refusal'
 * > from a future defect into a build error — and it is the actual disease: five
 * > refusals accumulated and nobody noticed the client could not tell them
 * > apart."*
 *
 * ── The three-valued disposition, Ivy `G.6` ────────────────────────────────
 *
 * Her amendment to `G.5.3` criterion 1 — *"the per-member verdict is now
 * THREE-valued: `TWIN, CATALOGUED` · `TWIN, UNCATALOGUED` · `NO TWIN`"* — is
 * what the two `T6` entries below encode. `ROOM_FULL` and `SEAT_CLOSED` have
 * twins, but their twins' sentences are **raw literals in `apps/api`, outside
 * the catalogue and never Nora-gated**, and `D4`'s retrofit rider forbids
 * copying an uncatalogued literal to a second surface. So they are **DEFERRED**
 * behind the ledgered `RAW-LITERAL-RETROFIT` row, which *"retrofits the literal
 * ON ITS EXISTING SURFACE FIRST, and only then plumbs it"*.
 *
 * **The split is monotone-safe and that is checkable: all five compose `T6`
 * today.** After this row three improve and two are byte-identical to what they
 * already were — nothing regresses, and the deferral costs a player nothing they
 * are not already paying.
 *
 * **`NO_SEAT_RESERVATION`'s twin is a PANEL, not a toast** — the rejoin route's
 * `no_seat` refusal already composes `T5` with this exact title/body pair. Ivy:
 * *"the panel-shaped twin is a point in favour, not a complication… this
 * member's twin is already in the right medium and Nora's 'heard alone in a 4 s
 * toast' caveat does not even arise for it."*
 *
 * **`A3` discharged, and it was a real check rather than a formality.** Ivy
 * required the borrowed bytes to be read before plumbing, with a STOP to
 * Theo → Nora on *a time reference, a retry instruction, or a first-person-plural
 * system voice*. All four sentences read **CLEAN**; the instrument's own
 * positive control for the third arm is `room.error.body` — *"**We** can't load
 * this room right now"* — the very sentence `T9` replaces, which would have
 * stopped this row had it been the one borrowed.
 */
export const ROOM_JOIN_REFUSAL_COMPOSITION: Record<RoomJoinRefusal, RefusalComposition> = {
  /** TWIN, CATALOGUED — the rejoin route's `no_seat`, which composes `T5`. */
  NO_SEAT_RESERVATION: { screen: "T9", titleKey: "room.noSeat.title", bodyKey: "room.noSeat.body" },
  /** TWIN, CATALOGUED — `POST /rooms/join` 409 `already_in_live_game`. */
  ALREADY_IN_LIVE_GAME: { screen: "T9", titleKey: null, bodyKey: "lobby.join.alreadyInLiveGame" },
  /** TWIN, CATALOGUED — `POST /rooms/join` 409 `already_seated`. */
  ALREADY_SEATED: { screen: "T9", titleKey: null, bodyKey: "lobby.join.alreadySeatedHere" },
  /** TWIN, **UNCATALOGUED** — deferred to `RAW-LITERAL-RETROFIT`. Unchanged today. */
  ROOM_FULL: { screen: "T6" },
  /** TWIN, **UNCATALOGUED** — deferred to `RAW-LITERAL-RETROFIT`. Unchanged today. */
  SEAT_CLOSED: { screen: "T6" },
};

/** The composition a refusal selects, or `null` when the reason is not ours. */
export function refusalComposition(refusal: RoomJoinRefusal | null): RefusalComposition | null {
  return refusal ? (ROOM_JOIN_REFUSAL_COMPOSITION[refusal] ?? null) : null;
}

/** The terminal set — the states §8.5 exempts from the stale dim. */
const TERMINALS = new Set<RoomScreenState>(["T1", "T2", "T3", "T4", "T5", "T7", "T8", "T9"]);

/**
 * Terminal states **do not dim and do not auto-navigate** (§19 line 16).
 *
 * T6 is deliberately excluded: it is not terminal, it is a failure to load, and
 * it carries the in-content `Retry`.
 *
 * **`T9` IS included, and that is Ivy `G.7` amendment A1 in one line.**
 * *"`T9` is terminal and NOTHING mutates it under a mounted screen… When it
 * clears, `T9` must not re-body, not swap tone, not auto-dismiss, not route
 * anywhere, and not re-announce. The affordances stay live and the PLAYER
 * re-drives; the screen does not re-drive itself."* Membership here is what
 * gives it the no-dim, no-auto-navigate contract the other terminals have; the
 * refusal self-clears server-side in ~20–23 s and **the panel does not notice**.
 */
export function isTerminal(state: RoomScreenState): boolean {
  return TERMINALS.has(state);
}

/**
 * **Which screens the content freeze does not reach** (§8.5, LOCKED).
 *
 * Every terminal, **plus `results`**: at F the standings, the prize and the
 * credit are *final*, and dimming finished, paid-out facts would be a lie in the
 * opposite direction from the one S3 §19.1 fixed — it would tell the player that
 * settled facts are unreliable, and it would dim the one screen they may want to
 * screenshot. §8.5 names the exempt set as "F, T1–T4, T7" and it is narrow: T6
 * is **not** exempt, because it is a failure to load rather than a result.
 *
 * The coin chip is a separate question and a separate signal: it dims **on all
 * of these**, including the terminals (§11.2 exception 3 / lobby §20.4) — **but
 * only during a genuine outage** (`isExpectedClose` below, S2 §21.2). *"On a
 * stale terminal screen the chip is the only dimmed element, and that is not a
 * regression of §8.5"* — the standings are final, the wallet number is not.
 */
export function freezesContent(state: RoomScreenState): boolean {
  return !isTerminal(state) && state !== "results";
}

/**
 * The phases whose **arrival** is an explanation for the socket closing. `results`
 * is included and that is the point: a settled room disposes itself ten seconds
 * later, and the close is the disposal, not a network fault.
 *
 * ── The vocabulary, and why the compiler now checks it — A117 ──────────────
 *
 * Typed `ReadonlySet<RoomPhaseView>`: **the in-room vocabulary from
 * `@wr/game-core`**, which is the one the synced `phase` carries (the room state
 * machine sets it). The annotation is the whole point — a phase renamed in
 * `ROOM_PHASES` now fails this build *here*, at the copy, instead of leaving a
 * string that silently matches nothing.
 *
 * `completed` **was** a member and is gone with A117. It is the persisted
 * (`rooms.phase`) vocabulary, not an in-room phase, so it cannot arrive on this
 * input: the only caller passes `snapshot.phase` off the socket, and the cold
 * HTTP route — the one place the persisted vocabulary *is* read — reaches
 * `isExpectedClose` through `refusalExplained`, never through `phase`. It was
 * inert, and it was the kind of inert that reads as intent.
 *
 * The lookups below take the wire's bare `string` and cast at the call, because
 * *"a phase this build does not recognise"* is a case this file deliberately
 * answers (see the unknown-phase branch of `selectRoomState`) — the set's
 * membership is typed, the question asked of it stays open.
 */
const TERMINAL_PHASES: ReadonlySet<RoomPhaseView> = new Set(["results", "cancelled", "closed"]);

/**
 * **Is there an explanation for having no live socket? — S2 §21.2, LOCKED.**
 *
 * > *"The banner reports transport **health**. A socket that closed because the
 * > thing it was attached to ended is not unhealthy, and no banner renders for
 * > it. The same fact lifts the chip dim: an expected close leaves nothing stale
 * > to explain."*
 *
 * Quinn's T-222 check 1 is what this exists for: *"Can't reach the server right
 * now."* rendered over a game that had just paid out, because the room disposed
 * itself and the SDK's `onLeave` set the connection to `offline`. Every clause of
 * that sentence was false — the server was fine, nothing was unreachable, and the
 * socket closed **because** the room ended. The transport state was a *mechanical
 * consequence* being rendered as a *condition*.
 *
 * Two ways the client can hold an explanation, and they are the two routes into a
 * finished room:
 *
 *  - **It received a terminal phase.** The room told this client it was over
 *    before the socket went; §8.5's amended row is exactly this split ("a genuine
 *    outage" is *"reconnecting / disconnected **with no terminal phase
 *    received**"*).
 *  - **There was never a socket, and the server said why.** The cold HTTP rejoin
 *    (§9.9, A42): the refusal carried this room's facts, so the panel on screen is
 *    the server's own answer. A refusal that carried **nothing** — a dead network,
 *    a 401 — is the opposite case and deliberately not expected: that is T6, where
 *    we genuinely could not find out.
 *
 * The third case in §21.2's definition, a **consented leave**, is not reachable on
 * this screen: a consented leave is performed *by* navigating away (§3.1, LOCKED
 * rule 18), so the screen has unmounted and reports nothing at all.
 *
 * **`navigator.onLine === false` is deliberately absent here.** §21.2 lists it
 * under "unexpected", and it is a *device* fact: the chrome already gives it
 * precedence over every screen report (`useNetworkStatus`, "the device first,
 * then the screen"), so a screen re-deriving it would be a second listener for a
 * signal that is not the screen's to own.
 */
export function isExpectedClose({
  phase,
  refusalExplained,
}: {
  /** The **last received** phase, or `null` if no state ever arrived. */
  phase: string | null;
  /** A cold rejoin the server refused **with this room's facts** (§9.9). */
  refusalExplained: boolean;
}): boolean {
  if (phase !== null) return TERMINAL_PHASES.has(phase as RoomPhaseView);
  return refusalExplained;
}

/** "Pre-game" is `filling` + `ready_check`; "mid-game" is `round` +
 *  `between_rounds`. **There is no third bucket anywhere** (§9.0, LOCKED).
 *
 *  Both are `ReadonlySet<RoomPhaseView>` per A117 — subsets of `ROOM_PHASES`
 *  that the compiler checks against `@wr/game-core`, so §9.0's two buckets
 *  cannot drift from the machine that produces the phases. */
export const PRE_GAME_PHASES: ReadonlySet<RoomPhaseView> = new Set(["filling", "ready_check"]);
export const MID_GAME_PHASES: ReadonlySet<RoomPhaseView> = new Set(["round", "between_rounds"]);

export interface SelectStateInput {
  /** `null` when no room state has arrived at all. */
  state: GameRoomStateView | null;
  /**
   * **The server's own `4409` refusal reason, or `null`** — the input `T9` is
   * selected from (`SOCKET-REFUSAL-COPY`, T-698).
   *
   * It is **sticky for the life of the controller generation**: once the server
   * has refused this join, nothing that happens afterwards un-refuses it, and
   * `useGameRoom` never clears it except through `reset()`, which is a
   * deliberate navigation. That stickiness is not an implementation convenience
   * — it is Ivy `G.7` amendments A1 and A2, and she ruled them **spec sections
   * of this panel, not implementation details**:
   *
   * * **A1 — liveness.** The refusal self-clears server-side in ~20–23 s.
   *   *"When it clears, `T9` must not re-body, not swap tone, not auto-dismiss,
   *   not route anywhere, and not re-announce."* Nothing in this client is
   *   watching that window, and nothing may start.
   * * **A2 — recovery.** *"If the socket drops while `T9` is up, it must not be
   *   promoted to `T6`, to a reconnect spinner, or to a retry state. `T9`
   *   already answered the player's question; a later transport event does not
   *   un-answer it."* Rank 0 below is what makes that true by construction
   *   rather than by a guard somebody can forget.
   */
  joinRefusal?: RoomJoinRefusal | null;
  /** This client's Colyseus session id — how it finds its own seat (§17 note 6). */
  sessionId: string | null;
  /**
   * The SDK gave up: the socket is permanently gone and no further state will
   * arrive. Not the same as `reconnecting`, where the seat is still held.
   */
  seatLost?: boolean;
  /**
   * The room's phase **at the moment this client dropped**, frozen.
   *
   * ── Why this input exists, and it is a documented deviation ────────────────
   *
   * §11.4's recovery table says a grace expiry pre-game lands on **T4** and
   * mid-game on **T3**, and §9.7's "when is T7 actually seen" assumes the
   * transport recovers so the room can tell the client what happened. In this
   * stack it cannot: client-side matchmaking is off (`exposedMethods = []`,
   * architecture §3.3), `client.reconnect()` is one of those blocked matchmake
   * methods, and there is no route to re-enter a room by id. So when the grace
   * window expires, the socket dies and **no snapshot ever carries this
   * player's `seatOutcome`**.
   *
   * §19 line 28 bars deciding T3 vs T4 from "the room's **current** phase", and
   * this is not that: it is the phase at the instant the seat ended, which is
   * *precisely the input the server's own `refundOnExit(phase)` used* to decide
   * whether the money came back. The two cannot disagree, because they are the
   * same fact read at the same moment. The live-phase hazard the rule is about —
   * a `ready_check` dropout reading a room that is now three rounds deep — is
   * unreachable here, because a frozen phase does not advance.
   *
   * A snapshot that *does* carry an outcome always wins: this is the fallback
   * for the case where none can arrive, not an alternative to one.
   *
   * **T-214 note:** the real fix landed — `POST /api/v1/rooms/:id/rejoin`
   * (architecture §16f A42) — and it is `rejoinFacts` below. This input keeps its
   * job, which is narrower than it was: the socket died *while this screen was
   * mounted* and nobody has asked the server anything since. A cold reload no
   * longer reaches it.
   */
  phaseAtDrop?: string | null;
  /**
   * What the server said when it **refused** to let this player back in
   * (architecture §16f **A42**, the failure half).
   *
   * ── Why it exists, and why it may only be read when nothing else arrived ───
   *
   * A cold reload on `/room/:id` has no socket, no snapshot and therefore no
   * evidence of any kind: before A42 it landed on T6 for every room in every
   * state, including a room that had cancelled and refunded the player twenty
   * seconds earlier. These are `rooms.phase`, `rooms.cancel_reason` and the
   * participant's outcome, read off the persisted rows by the rejoin route.
   *
   * **A live snapshot always wins.** This is consulted only at rank 1a — "no
   * state arrived" — because a room that is talking to us is a better authority
   * about itself than a refusal we were handed a moment ago. It is a fallback for
   * the case where no snapshot *can* arrive, never an alternative to one.
   *
   * `null` is the honest absence and it still means **T6**: we could not find
   * out. §9.7's rules survive intact underneath — an unknown cancel reason is
   * T1 and never T7, and an ended seat with no outcome is T6 rather than a guess
   * about money.
   */
  rejoinFacts?: RejoinRefusalFacts | null;
}

/**
 * **The room half of a refusal — the canonical `RoomSummary`** (architecture
 * §16f A45).
 *
 * Facts about the room and **nothing about any player**. `phase` is the
 * **`rooms.phase`** vocabulary, not the in-room one — the room object may be long
 * gone. The two money values are the same `rooms.*_snapshot` the live room sends
 * (§5.4 LOCKED), which is what makes the refused frame and the live frame
 * incapable of disagreeing: every terminal's copy states what happened to the
 * entry, and a cold reload has no snapshot of its own to read.
 */
export interface RoomSummaryFacts {
  /**
   * `rooms.id` — the spent-room set's space (lobby §6.7, A41), so a terminal
   * reached by cold reload still makes the lobby tile true before the next tap.
   */
  roomId: string;
  /** What `Join again` / `Play again` re-join: the config, never this room. */
  roomConfigId: string;
  gameTypeName: string;
  gameModeName: string;
  entryFeeCoins: number;
  /** **The room's** prize, in total — never anybody's credit (§17 note 9). */
  prizeCoins: number;
  phase: string;
  /**
   * `rooms.cancel_reason` or **`null`** — the column's absence, carried as an
   * absence (T-217). `all_disconnected` is the only value that may produce T7;
   * everything else, `null` included, is T1.
   */
  cancelReason: string | null;
}

/**
 * The persisted facts a refused rejoin carries: the room's summary, and the
 * viewer's own two facts **as siblings of it, never members** (A45, LOCKED).
 */
export interface RejoinRefusalFacts {
  /** The refusal's discriminator. `no_seat` is §9.7 rank 2 and nothing else is. */
  reason: string;
  room: RoomSummaryFacts;
  /**
   * `released` | `forfeited` | **`null`** — "this seat has not ended, or we
   * cannot say how" (T-217). On a `completed` room the absence is T8's only
   * input (§9.9); anywhere else it is T6.
   */
  seatOutcome: string | null;
  /**
   * **`me.prizeCoins` — what this player was *credited* in this room**, from
   * `prize_txn_id` → `amount_delta` and from nothing else (A45 addendum).
   *
   * `null` = the server cannot say, and on a **paid** room that renders **T6**
   * (§9.9 LOCKED) rather than a guess. It is **never rendered as `0`** (§8.3):
   * `0` is the different, settled statement *this room credited you nothing*,
   * which is T8's `prize_other` branch.
   */
  prizeCoins: number | null;
}

/**
 * **§9.7's precedence table, in order, and it is LOCKED.**
 *
 * | Rank | Condition | Panel |
 * |---:|---|---|
 * | 1 | No state arrived — **or** state arrived, this seat has ended, and the server did not say **how** | T6 |
 * | 2 | State arrived, this player has **no participant record in this room at all** | T5 |
 * | 3 | `cancelled` **and** `cancelReason === 'all_disconnected'` | T7 |
 * | 4 | `cancelled`, any other or absent reason | T1 |
 * | 5 | `closed` | T2 |
 * | 6 | This seat ended in `round` / `between_rounds` (`seatOutcome === 'forfeited'`) | T3 |
 * | 7 | This seat ended in `filling` / `ready_check` (`seatOutcome === 'released'`) | T4 |
 * | 8 | `completed` **and** no seat outcome — the game ran to the end with this player's seat in it | T8 |
 *
 * Four properties of that order are the whole point and each is a LOCKED note:
 *
 *  - **A room-level terminal outranks this player's own participant outcome**
 *    (ranks 3–5 beat 6–7). A player who forfeited into an `all_disconnected`
 *    room sees **T7** and is told no loss was taken — A31 makes `cancel_reason`
 *    the authority for "no result stands", so a forfeit panel here would tell a
 *    player they took a loss the ledger and their stats say they did not.
 *  - **Ranks 6 and 7 are decided by the server's recorded outcome, never by the
 *    room's current phase** (§19 line 28). A seat that ended during
 *    `ready_check` is refunded, and by the time that player looks the room may
 *    be three rounds deep — reading the live phase would show T3 to a refunded
 *    player.
 *  - **Unknown cause falls back to T1, never to T7.** T1's refund statement is
 *    true on every cancel path, so the money copy is safe either way; T7's
 *    claims are specific — *everyone dropped*, *this doesn't count* — and
 *    specific claims are only made when the server made them. **Never guess T7.**
 *  - **Ranks 6 and 7 beat rank 8, which is the *opposite* of the rule above and
 *    is deliberate** (§9.7, LOCKED, added 2026-07-29). `cancelled` and `closed`
 *    are room-level statements that **override** what happened to this player
 *    (A31: nothing counts, everyone is refunded), so they outrank the
 *    participant outcome. `completed` makes no such claim — it says only that
 *    the game ran to the end, which is perfectly compatible with this player
 *    having forfeited during it. So a forfeited seat in a completed room is
 *    **T3** and a pre-game-released seat in a completed room is **T4**: their own
 *    outcome is the truer, more specific answer, and T8 would hide a refund or a
 *    loss behind "this game is over". **`completed` is the only terminal phase
 *    the participant outcome outranks.**
 *
 * Rank 2 means "never in this room", not "no live seat": a player whose seat was
 * released or forfeited still has a record here and still reads ranks 6–7.
 */
export function selectRoomState({
  state,
  sessionId,
  seatLost = false,
  phaseAtDrop = null,
  rejoinFacts = null,
  joinRefusal = null,
}: SelectStateInput): RoomScreenState {
  /**
   * **Rank 0 — the server named its reason, and a named reason outranks every
   * inference below it** (`SOCKET-REFUSAL-COPY`, T-698; Ivy `G.7`).
   *
   * This sits *above* rank 1a rather than inside it, and the position is the
   * whole of amendments A1 and A2. A socket refused at `onAuth`/`onJoin` yields
   * no state, so in practice `state` is already `null` here — **but ranking the
   * refusal first means no later event can outrank it.** A transport drop, a
   * reconnect attempt or a stray snapshot cannot promote a mounted `T9` back to
   * `T6` or to a reconnect state, because they are all evaluated after this
   * line and never reach it.
   *
   * A refusal whose composition is `T6` (the two `RAW-LITERAL-RETROFIT`
   * deferrals) falls through deliberately: it keeps composing exactly what it
   * composes today, which is what makes the three-member split monotone-safe.
   */
  const refusal = refusalComposition(joinRefusal);
  if (refusal?.screen === "T9") return "T9";

  // Rank 1a — nothing arrived. The server's refusal is the only evidence there
  // can be here, and with none of it this is still T6 (A42).
  if (!state) return rejoinFacts ? terminalFromRejoin(rejoinFacts) : "T6";

  const seat = mySeat(state, sessionId);
  const phase = state.phase;

  // Rank 2 — no participant record here at all. `me === null` is T5's ONLY input.
  if (!seat) return "T5";

  const ended = seat.status === "left" || seat.status === "forfeited";

  // Ranks 3–5 — the room-level terminals, which outrank the participant outcome.
  if (phase === "cancelled") {
    return state.cancelReason === "all_disconnected" ? "T7" : "T1";
  }
  if (phase === "closed") return "T2";

  if (ended) {
    /**
     * Rank 1b — **the deliberate widening of T6** (§17 note 8): state arrived
     * without the fact this screen needs. There is no safe guess: T4 claims a
     * refund and T3 claims a loss, so a wrong default lies about coins in one
     * direction or the other and the coin chip will contradict it on screen.
     * "We can't load this room right now" is literally true when we have the
     * room but not the player's outcome.
     */
    if (seat.seatOutcome === "forfeited") return "T3";
    if (seat.seatOutcome === "released") return "T4";
    return "T6";
  }

  /**
   * The socket is permanently gone and the last snapshot still shows a live
   * seat, because the server ended it after we stopped hearing from it. The
   * frozen phase is the only evidence that exists, and it is the same evidence
   * the ledger used — see `phaseAtDrop`'s note.
   */
  if (seatLost) {
    /**
     * ── A finished game stays finished when its room closes — T-219 (Quinn R1) ─
     *
     * `results` is not a seat that ended without us hearing how: it is **the
     * end of the game**, in a snapshot that already carries the standings, the
     * winner and this player's credited prize. The socket dying afterwards is
     * not new information — it is what a terminal room *does*, by design, ten
     * seconds after settling (`BaseGameRoom.lingerThenDispose`), and it happens
     * to every player who stays on the screen long enough to read it.
     *
     * Without this line the room screen answered its own money moment with T6 —
     * *"We can't load this room right now."* and a `Retry` over a credited prize
     * — because `results` is in neither `MID_GAME_PHASES` nor `PRE_GAME_PHASES`
     * and fell to the "we know nothing" default. It was masked while nothing
     * disposed a lingering room; Quinn's R1 un-masked it and the server half of
     * that fix (the orphan sweep's linger guard) only moves the moment, from
     * ~1 s to the honest ten. §19 line 16 (*terminals do not dim and do not
     * auto-navigate*) and §9.9 both say the standings survive; T6 asserts a
     * failure that did not happen.
     *
     * The screen it lands on is therefore the **frozen** results screen: the
     * standings and prize stay bright (§8.5 exempts `results` from the dim), the
     * banner is status-only (`freezesContent('results')` is false, so no
     * `Retry` — Ivy §21.1), and the coin chip dims while holding its value,
     * which is the one thing on a settled screen that is not final.
     *
     * It is deliberately narrow: only `results`, and only from the *snapshot's*
     * phase, never from `phaseAtDrop`. A client that dropped mid-round and never
     * saw the end has a `round` snapshot and still gets T3.
     */
    if (phase === "results") return "results";
    if (phaseAtDrop !== null && MID_GAME_PHASES.has(phaseAtDrop as RoomPhaseView)) return "T3";
    if (phaseAtDrop !== null && PRE_GAME_PHASES.has(phaseAtDrop as RoomPhaseView)) return "T4";
    // No frozen phase either: we know nothing, so we claim nothing.
    return "T6";
  }

  switch (phase) {
    case "filling":
    case "ready_check":
      return "waiting";
    case "round":
      return "round";
    case "between_rounds":
      return "between";
    case "results":
      return "results";
    default:
      // A phase this build does not recognise is not a state we may invent one
      // for. T6 makes no claim about the player's money.
      return "T6";
  }
}

/**
 * **§9.7's table again, from persisted facts instead of from live state** — the
 * client half of architecture §16f A42's informative refusal.
 *
 * It is the same order, deliberately, because it is the same question asked of a
 * room that will not talk to us. Three details are where the two vocabularies
 * differ and each one is a decision, not a translation:
 *
 *  - **`admin_close` is T2.** The live path reads a state phase of `closed`,
 *    which the room sets in `forceClose`; `rooms.phase` has no such value and
 *    records `cancelled` + `cancel_reason = 'admin_close'` instead. Same event,
 *    same panel (§9.7 rank 5), and mapping it here rather than widening the wire
 *    keeps the persisted vocabulary honest.
 *  - **`all_disconnected` is the only reason that may produce T7**, exactly as
 *    on the live path. Every other cancel reason — including one this build has
 *    never heard of — is T1, whose refund statement is true on every cancel path.
 *    **Never guess T7.**
 *  - **`completed` is T8, and rank 8 is *below* ranks 6–7** (§9.9, added
 *    2026-07-29 — T-214 flagged this as a design gap and Ivy ruled it). The game
 *    finished and the room is gone; the panel says so and states what happened
 *    to the money. It was T6 until this ruling — *"we can't load this room right
 *    now"* to a player whose game finished normally and who may have been **paid
 *    for winning it**, with the coin chip on screen beside it.
 */
function terminalFromRejoin(facts: RejoinRefusalFacts): RoomScreenState {
  // Rank 2, and it outranks the room's own terminal exactly as it does on the
  // live path: a player who was never here is told that, whatever the room did.
  if (facts.reason === "no_seat") return "T5";

  // Ranks 3–5 — the room-level terminals beat this player's own outcome.
  if (facts.room.phase === "cancelled") {
    if (facts.room.cancelReason === "all_disconnected") return "T7";
    if (facts.room.cancelReason === "admin_close") return "T2";
    return "T1";
  }

  // Ranks 6–7 — the server's recorded outcome, never the room's current phase.
  // **These two beat rank 8**, which is the inversion §9.7 locks: a forfeited or
  // released seat in a completed room reads its own, truer outcome, because T8
  // would hide a loss or a refund behind "this game is over".
  if (facts.seatOutcome === "forfeited") return "T3";
  if (facts.seatOutcome === "released") return "T4";

  /**
   * **Rank 8 — T8** (§9.9). An absent `seatOutcome` on a `completed` room is not
   * "unknown": it is *this seat never ended early*, and it is T8's only input.
   *
   * **The one safety net, LOCKED:** on a **paid** room a missing `me.prizeCoins`
   * renders **T6**. This panel exists to make a money statement, and a build
   * that cannot make it has "state arrived without the fact this screen needs",
   * which is what T6 was widened to mean (§17 note 8's discipline). A free room
   * renders no money line at all, so it needs no such fact and never falls here.
   *
   * **T2 may never render for `completed`** (§9.9 LOCKED): T2's copy says *we*
   * closed the room and the entry came back, and after a normal game both halves
   * are false — one of them about money. `closed` and `completed` are different
   * phases and this function keeps them so.
   */
  if (facts.room.phase === "completed") {
    if (facts.room.prizeCoins > 0 && facts.prizeCoins === null) return "T6";
    return "T8";
  }

  return "T6";
}

/** The viewer's own seat, found by `sessionId` — the only identifier they have. */
export function mySeat(
  state: GameRoomStateView | null,
  sessionId: string | null,
): GameSeatView | null {
  if (!state || !sessionId) return null;
  let found: GameSeatView | null = null;
  state.seats?.forEach((seat) => {
    if (seat.sessionId === sessionId) found = seat;
  });
  return found;
}

/**
 * The viewer's own `seatIndex`, or `null`.
 *
 * The board and the score strip both need it and neither may derive it from
 * anything but `sessionId` — the only identifier this client has for itself
 * (§17: a player's real id never crosses the wire).
 */
export function mySeatIndex(
  state: GameRoomStateView | null,
  sessionId: string | null,
): number | null {
  return mySeat(state, sessionId)?.seatIndex ?? null;
}

/**
 * **`between_rounds_ms`, derived from the room's own clocks** (game spec §9).
 *
 * The value feeds `revealHoldMs = min(2000, betweenRoundsMs × 0.4)` and it is not
 * on the wire as a number — but `nextRoundAt` is, and the room arms it as
 * `now + between_rounds_ms` at the instant it leaves a round. So the window is
 * `nextRoundAt − serverTime` **on the patch that carried it**, which is exactly
 * the moment the board needs it.
 *
 * `null` means *"not derivable yet"* — during a live round there is no
 * `nextRoundAt`. The reveal hold treats that as its cap (2000ms), which is the
 * safe direction: a derived value can only ever shorten the hold, so an unknown
 * one can never let it eat a configured between-rounds screen.
 */
export function betweenRoundsMsFrom(
  state: {
    nextRoundAt: string;
    serverTime: string;
  } | null,
): number | null {
  if (!state?.nextRoundAt || !state.serverTime) return null;
  const next = Date.parse(state.nextRoundAt);
  const serverNow = Date.parse(state.serverTime);
  if (!Number.isFinite(next) || !Number.isFinite(serverNow)) return null;
  const window = next - serverNow;
  return window > 0 ? window : null;
}

/* ==========================================================================
   Countdowns and the server clock (§17 note 1)
   ========================================================================== */

/**
 * An absolute server instant, translated into the **client's** clock.
 *
 * §17 note 1: every deadline is an absolute instant plus a `serverTime`
 * reference, never remaining-milliseconds. The skew correction is
 * `deadline − serverTime + (the client's own now)`, taken at the moment the
 * patch arrived — so a device with a wrong clock still renders the right number
 * and a tab that slept renders the right number too.
 *
 * `null` for an absent deadline (`""`), an unparseable instant, or a missing
 * server reference: a countdown we cannot place is a countdown we do not draw.
 */
export function deadlineToClientClock(
  isoDeadline: string,
  isoServerTime: string,
  receivedAt: number,
): number | null {
  if (!isoDeadline || !isoServerTime) return null;
  const deadline = Date.parse(isoDeadline);
  const serverNow = Date.parse(isoServerTime);
  if (!Number.isFinite(deadline) || !Number.isFinite(serverNow)) return null;
  return receivedAt + (deadline - serverNow);
}

/**
 * **The one UI constant this screen introduces** (§6.4, §18).
 *
 * `fillWarningMs = min(120_000, fillTimeoutMs / 4)`. It is a **presentation
 * threshold, not a product value**: it goes in the client's design constants,
 * never in `platform_settings`, never in copy, and never inline at the call
 * site.
 *
 * What it buys: a 15:00 countdown on arrival tells a new player two things we do
 * not want to say first — that this table is expected to sit empty, and that
 * they should watch a clock instead of the room. Inside the window the line
 * appears; before it, there is no countdown, no clock, nothing. **It
 * self-resolves: when tables fill in seconds the line never appears at all.**
 */
export const FILL_WARNING_CAP_MS = 120_000;

export function fillWarningMs(fillTimeoutMs: number): number {
  return Math.min(FILL_WARNING_CAP_MS, Math.max(0, fillTimeoutMs) / 4);
}

/**
 * Is the fill deadline inside its warning window?
 *
 * The window is derived from the *timeout*, which the client does not receive
 * directly — it receives the deadline. The room's own `fill_timeout_ms` is
 * reconstructible only if we know when the clock started, so the honest input is
 * the remaining time against a window the caller computed from config. The
 * caller passes both; this function makes the comparison one testable place.
 */
export function inFillWarningWindow(remainingMs: number | null, windowMs: number): boolean {
  return remainingMs !== null && remainingMs <= windowMs;
}

/* ==========================================================================
   Seats, teams and the status vocabulary (§5, §11.1)
   ========================================================================== */

interface StatusWord {
  label: string;
  tone: StatusTone;
  glyph: "check" | "cross" | "dot" | null;
}

/**
 * §11.1's LOCKED vocabulary — **one word set for the phase chip, the seat pills
 * and the scoreboard**, so a player never learns two names for one fact.
 *
 * Colour is never alone: `ready` and `forfeited` carry a glyph as well as a
 * word, and every entry carries the word.
 */
export function seatStatus(status: string): StatusWord | null {
  switch (status) {
    case "ready":
      return { label: t("room.status.ready"), tone: "good", glyph: "check" };
    case "waiting":
      return { label: t("room.status.waiting"), tone: "ink", glyph: null };
    case "reconnecting":
      return { label: t("room.status.reconnecting"), tone: "coin", glyph: "dot" };
    case "left":
      return { label: t("room.status.left"), tone: "quiet", glyph: null };
    case "forfeited":
      return { label: t("room.status.forfeited"), tone: "warn", glyph: "cross" };
    default:
      // `empty` — there is nobody to have a status (§6.2).
      return null;
  }
}

/**
 * **The room's shape**, from its state — the one input `@wr/contracts`'
 * three side predicates take (A132 §3, BINDING: *"one object and not two
 * integers … TypeScript cannot catch a swap among them"*).
 *
 * Written once, here, so no call site in this file spells the object inline and
 * gets the two fields the wrong way round. **`tsc` cannot see that swap** —
 * Q512-F1, proven at three `apps/game` call sites — so every consumer below is
 * driven by at least one fixture whose `teamCount !== teamSize`.
 *
 * **Exported at S12 R3, for Q514-F2.** T-514's QA found the two `RoomScreen.tsx`
 * literals this helper was written to prevent (`shape={{ teamCount: …,
 * teamSize: … }}` at the round wrapper, and the `sideName(index, { … })` at the
 * seat block), drove both transpositions, and measured that `tsc --noEmit`
 * exits 0 on each: *"Fix shape for R3: route those two through
 * `roomShape(snapshot)`."* Both now do, so **this is the only construction site
 * for a `RoomShape` in `apps/web`'s product code** — which is what makes the
 * one guard in `room.test.ts` a guard on all of it rather than on one caller.
 */
export function roomShape(state: { teamCount: number; teamSize: number }): RoomShape {
  return { teamCount: state.teamCount, teamSize: state.teamSize };
}

/**
 * **The side's name, or `null` — the replacement for the retired
 * `teamLabel()`/`teamLetter()` pair** (D1 Decision 1, S12 R2).
 *
 * > *"The letter-generation mechanism itself — not just its output string — is
 * > what SIDES exists to retire; a rename would keep the wrong mechanism alive
 * > under a new label."*
 *
 * **Two gates, deliberately kept apart — Quinn's T-512 trap, verbatim:**
 * *"side **beds** follow the sides predicate; side **names** follow the names
 * gate. Do not conflate them."* Both must hold for a name to exist at all, and
 * they are two different questions with two different reasons:
 *
 *  1. **`teamSize > 1` — the NAMES gate. LOCKED (§5): "a team of one is a
 *     player."** A 1v1 (`2 × 1`) and a `1v1v1v1` (`4 × 1`) both **have sides**
 *     — the table names and colours them, and their seats wear the side colour
 *     on the avatar ring — and neither gets a printed side name, because
 *     labelling one player "Blue" is chrome pretending to be information.
 *  2. **`roomHasSides(shape)` — the SIDES gate (A132).** A royale
 *     (`> 4 × 1`) and a single-camp room (`1 × N`) have no sides at all, so
 *     there is no row of the frozen table to name. `sideIndexForTeam` **is**
 *     that gate — it answers {@link NO_SIDE} — so this function asks it rather
 *     than re-spelling the predicate.
 *
 * Total over any `teamIndex`: a value the four-row table does not have (only
 * reachable from a `> 4 × > 1` room, which `assertSupportedSideCount` refuses
 * at spawn) answers `null` and the caller renders no name, rather than
 * inventing a fifth word. There is no "Team Ochre" path here either.
 */
export function sideName(teamIndex: number, shape: RoomShape): string | null {
  if (shape.teamSize <= 1) return null;
  /**
   * **The table is indexed directly rather than through `sideForIndex`, and the
   * reason is a type join rather than a preference.** `SIDES` is declared
   * `as const satisfies readonly SideRow[]`, so `SIDES[n].nameKey` is the union
   * of the four **literal** keys and `t()` accepts it with no cast.
   * `sideForIndex()` returns the widened `SideRow`, whose `nameKey` is
   * `` `side.${number}.name` `` — deliberately loose, because `@wr/contracts`
   * may not depend on `@wr/i18n` to hold a key — and would need one. The cast is
   * what a renamed key would slip through; this way the compiler carries half of
   * what `lib/sides-copy.test.ts` asserts at runtime.
   *
   * `sideIndexForTeam` answers `NO_SIDE` (`-1`) in a room with no sides and for
   * the never-occupied `teamIndex` sentinel, and `SIDES[-1]` is `undefined`, so
   * the lookup is total with no range guard.
   */
  const row = SIDES[sideIndexForTeam(teamIndex, shape)];
  return row === undefined ? null : t(row.nameKey);
}

/**
 * **The side's colour index, or `null` — the one resolution every bedded
 * surface in the product reads** (S12 R3, extracted from R2's `standingTeams`).
 *
 * *"Every new coloured object names the provenance of its colour"* (amendment
 * 3.4 / D3a §7.3). This function **is** that provenance, spelled once:
 *
 *     teamIndex → sideIndexForTeam(shape) → SIDES[sideIndex].colorIndex
 *                                         → IDENTITY_COLOURS[colorIndex]
 *
 * Ivy's four frozen rows (D3a §1) supply the middle step, and it is the same
 * lookup the **server** performs when it stamps `colorIndex` onto every seat of
 * that side (`sideColourIndex` in `@wr/contracts`, called from
 * `resolveSeatColour`). So a bed, the seat dots inside it, the members' avatar
 * rings and their claims on the board are one value arrived at by one route.
 *
 * **Deliberately paired with, and deliberately different from, {@link sideName}
 * — they ask different gates and this is the split Quinn's T-512 trap names.**
 *
 *  - `sideName` asks **both** the NAMES gate (`teamSize > 1`, LOCKED — a team
 *    of one is a player) **and** the SIDES gate.
 *  - `sideColour` asks the **SIDES gate only** (`sideIndexForTeam`, which *is*
 *    that gate). A 1v1 has two sides: no printed name, two real colours. That
 *    disagreement is the point, not an oversight — R3's DoD asks for exactly it
 *    (*"1v1 (colours, no side names)"*), and any surface that derived colour
 *    from the label would render a 1v1 grey.
 *
 * Total over any `teamIndex`, including the `-1` sentinel and a value from a
 * newer server: `sideIndexForTeam` answers `NO_SIDE` and `sideColourIndex`
 * answers `null`, so a room with no sides (a royale, a single camp) beds
 * nothing. Fail-closed, which is the required direction — an unknown side
 * paints no colour rather than guessing one.
 *
 * **`null` never means "grey is this side's colour"**; it means *this object
 * carries no side bed*, which is also the predicate D3a §2 scopes the
 * `--wr-good` demotion by.
 */
export function sideColour(teamIndex: number, shape: RoomShape): number | null {
  return sideColourIndex(sideIndexForTeam(teamIndex, shape));
}

export interface SeatViewInput {
  seat: GameSeatView;
  sessionId: string | null;
}

/**
 * The seat row's accessible name — **one key per row shape** (Ivy §22.8.2).
 *
 * Three arms, a pure `if` on `(empty, isYou)`, no concatenation and no
 * interpolation of one resolved string into another. Two defects die here:
 *
 *  1. the viewer could not tell which seat was theirs — §6.2 LOCKED `You` as a
 *     word, and the word was the one thing the single key omitted;
 *  2. an empty slot announced *"Seat 3: Open seat, Open seat."*, because the
 *     builder passed `room.seat.open` as both `{nickname}` and `{status}`.
 *
 * **§22.8.5: the label must be a pure function of `(seatIndex, nickname, isYou,
 * status)`.** The seat `<ul>` is `aria-live="polite"` and is the only live
 * region in the room's content, so changing a label re-announces that item —
 * which is what we want when a seat fills, readies, disconnects or empties, and
 * exactly why nothing that ticks may enter it. Anything else in this function's
 * inputs would let an unrelated re-render re-announce a seat that did not
 * change.
 *
 * **§22.8.4, what must never appear:** coin amounts of any kind, player/session/
 * room ids, the seat's colour or any avatar description, a countdown or seconds
 * value, a score, or capacity phrasing ("seat 1 of 4"). None of them are
 * displayed on this row, and an accessible name may not introduce a fact the row
 * does not display.
 */
function seatA11yName(
  seatIndex: number,
  nickname: string | null,
  isYou: boolean,
  isBot: boolean,
  status: string,
) {
  const seat = seatIndex + 1;
  if (nickname === null) return t("room.seat.a11y.open", { seat });
  /**
   * **The bot arm comes first, and it is the §3.2 tie-break restated at the
   * label** (bots §5): a bot is never the viewer, so the two are mutually
   * exclusive by construction — but if both were ever true, the disclosure wins.
   * §22.8.2's clause order is unchanged: position → identity → **marker** →
   * state, with the bot clause exactly where the viewer clause sits, because it
   * is the same kind of clause in the same visual position.
   */
  if (isBot) return t("room.seat.a11y.bot", { seat, nickname, status });
  if (isYou) return t("room.seat.a11y.you", { seat, nickname, status });
  return t("room.seat.a11y", { seat, nickname, status });
}

/** One seat, as `@wr/ui` renders it. The a11y name is one parameterised string. */
export function seatView({ seat, sessionId }: SeatViewInput): SeatView {
  const status = seatStatus(seat.status);
  const empty = seat.status === "empty" || seat.nickname === "";
  const nickname = empty ? null : seat.nickname;
  const isYou = sessionId !== null && seat.sessionId === sessionId;
  /**
   * **`isBot` is the only carrier** (A81, LOCKED): never the `bot:` session id's
   * shape, never the `bot_` nickname prefix. The name is an independent
   * *backstop* for surfaces the chip cannot reach — reading it as the input
   * would collapse two carriers into one and hand the whole model to whoever
   * first ships a nickname bug.
   */
  const isBot = seat.isBot === true && !empty;

  return {
    seatIndex: seat.seatIndex,
    nickname,
    avatarUrl: seat.avatarUrl,
    colorIndex: seat.colorIndex,
    statusLabel: status?.label ?? null,
    statusTone: status?.tone ?? "quiet",
    statusGlyph: status?.glyph ?? null,
    isYou,
    isBot,
    /**
     * `{status}` is fed **only** from `seatStatus()`'s resolved word (§22.8.2).
     * The `?? ""` is the version-skew case and nothing else: `status` is
     * `string` on the wire, so a server that grew a sixth status word would
     * resolve to `null` here. An empty clause states nothing, which is the right
     * answer — a fallback word would put a status on the screen reader that the
     * pill, which renders nothing for an unresolved word, does not show.
     */
    a11yName: seatA11yName(seat.seatIndex, nickname, isYou, isBot, status?.label ?? ""),
    dimmed: seat.status === "forfeited" || seat.status === "left",
  };
}

/* ==========================================================================
   Bots — the offer, and the one derived room-level fact (bots spec §4, §9)
   ========================================================================== */

/**
 * **Does this room contain a bot?** — Ivy §4.8's `containsBots`, and it is
 * **derived, not a wire field** (A83: *"nothing is added at room level"*).
 *
 * `seats.some(isBot)` over an array the client already holds in full (§17 note
 * 5). A room-level flag would be a second answer capable of disagreeing with the
 * seats under a partial patch — and it is the seats that render.
 *
 * It may go `false → true` exactly once under a mounted screen and **never
 * back**: a bot never leaves a room.
 */
export function containsBots(state: GameRoomStateView | null): boolean {
  if (!state) return false;
  let found = false;
  state.seats?.forEach((seat) => {
    if (seat.isBot) found = true;
  });
  return found;
}

export interface BotOfferView {
  /**
   * The card renders. **Entirely the server's answer** (A83): phase, empty
   * seats, the fee, the kill switch, engine support, the density floor and the
   * `max(room's first seating, this viewer's own seating)` delay are all
   * evaluated there, per seat, and arrive as one boolean. The client re-derives
   * none of it — which is what makes Ivy §14 items 1 and 2 testable from
   * published state alone.
   */
  available: boolean;
  /** This viewer has accepted. One-way: it never returns to `false` (§9). */
  accepted: boolean;
  /**
   * Occupied **human** seats that have not accepted, excluding this viewer.
   * Renders `room.bots.offerWaiting`'s `{n}`, and only at `n ≥ 1` — at 0 the
   * fill has fired and the room has left `filling`.
   */
  outstanding: number;
  /** Empty seats — `room.bots.offerCta`'s `{n}`. Bots fill **every** one (§11). */
  emptySeats: number;
}

/**
 * **The offer card's whole input** (bots §4.3, §4.4).
 *
 * `null` when there is nothing to render: no state, no seat of our own, or the
 * server says the offer is not available to this seat. The card unmounts the
 * moment availability stops holding — *"a human join always beats a pending
 * offer. No apology, no toast; the room filled, which is what the player
 * wanted."*
 *
 * The one thing this function must never do is decide the offer is available
 * when the server did not say so. Every other value here is a count the client
 * can take from the same array the seats render from.
 */
export function botOffer(
  state: GameRoomStateView | null,
  sessionId: string | null,
): BotOfferView | null {
  const seat = mySeat(state, sessionId);
  if (!state || !seat) return null;
  if (!seat.botOfferAvailable) return null;

  let outstanding = 0;
  let emptySeats = 0;
  state.seats?.forEach((other) => {
    const occupied = other.status !== "empty" && other.nickname !== "";
    if (!occupied) {
      emptySeats += 1;
      return;
    }
    // Ivy §4.4: unanimity is over the occupied HUMAN seats. A bot has no vote,
    // and this viewer's own vote is the card's state rather than a count.
    if (other.isBot) return;
    if (other.seatIndex === seat.seatIndex) return;
    if (!other.botOfferAccepted) outstanding += 1;
  });

  return {
    available: true,
    accepted: seat.botOfferAccepted === true,
    outstanding,
    emptySeats,
  };
}

/**
 * **The alone-note's render predicate — Ivy §22.19.9, RULED verbatim.**
 *
 * `room.waiting.aloneNote` ("You got here first.") is the offer's **complement,
 * not its prelude**: `botOfferPossible === false` is its **trigger**, not its
 * veto. So it renders where an offer will never come — mainly a funded player
 * seated alone at a paid table, where rule 32(a) makes `botOfferPossible` false
 * forever — and it stays quiet in the 12s pre-offer window of a free 1v1.
 *
 * ```
 * render iff  my seat && seat.occupied
 *         &&  phase === 'filling'
 *         &&  playerCount === 1          ← the note's own premise: you are alone
 *         && !botOfferPossible           ← the trigger
 *         && !botOfferAvailable          ← the absent-field belt (LOCKED, required)
 *         &&  no fill-deadline warning active   (§6.4 owns that slot)
 * ```
 *
 * **Two LOCKs verified present, do not simplify away (§22.19.9):**
 *
 *  - `!botOfferAvailable` is redundant under A114's invariant `available ⇒
 *    possible`, but is **required** as the belt for the absent-field hazard: a
 *    new `web` against an old `game` sees `botOfferPossible` absent, which
 *    `=== true` reads as `false` (A114 §6 fail-closed default), so under the
 *    inverted trigger the note would render — the belt bounds that mis-sequenced
 *    render to the pre-card window, keeping the note off the screen beside a live
 *    offer card in every state.
 *  - The note therefore **never renders beside the offer card**, by construction.
 *
 * **Fail-closed default (A114 §6):** `botOfferPossible` is read as `=== true`, so
 * an absent/`undefined` field is `false`. Under §22.19.9's inverted trigger that
 * means a mis-deploy window shows the note — a *true* sentence shown
 * unnecessarily, not a false one, which §22.19.9 rules **licensed**. (This is the
 * one place A114 item 9's original *"absent ⇒ no note"* is superseded: the trigger
 * was inverted after A114, and the ticket's own Task text and item 6 say so.)
 */
export function aloneNoteVisible(
  state: GameRoomStateView | null,
  sessionId: string | null,
  fillWarningActive: boolean,
): boolean {
  const seat = mySeat(state, sessionId);
  if (!state || !seat) return false;
  // my seat && occupied
  if (seat.status === "empty" || seat.nickname === "") return false;
  // phase === 'filling'
  if (state.phase !== "filling") return false;
  // playerCount === 1 — you are alone (the string's premise)
  let players = 0;
  state.seats?.forEach((s) => {
    if (s.status !== "empty" && s.nickname !== "") players += 1;
  });
  if (players !== 1) return false;
  // !botOfferPossible — the TRIGGER. `=== true` is the fail-closed read: absent ⇒
  // false ⇒ trigger fires (§22.19.9 licensed).
  if (seat.botOfferPossible === true) return false;
  // !botOfferAvailable — the belt (LOCKED, required, never simplify away).
  if (seat.botOfferAvailable === true) return false;
  // §6.4 owns the slot inside the fill-deadline warning window.
  if (fillWarningActive) return false;
  return true;
}

/**
 * `team_count` blocks of `team_size` seats, in `seatIndex` order inside each —
 * **or one flat block when the room has no sides.**
 *
 * **LOCKED (§6.2): seats never re-order and are never removed while mounted.**
 * The grouping is by `teamIndex` on the *slot* — teams are seat-shaped, so an
 * empty slot still knows which block it belongs to, which it does by position
 * rather than by its (`-1`) team index.
 *
 * **A132's paste-in clause, at its first consumer:** *"the results split's
 * predicate is the sides predicate from `@wr/contracts`, never
 * `teamCount > 1`. A room with no sides renders the flat/FFA standings row …
 * and a ten-seat royale is a driven case of that, not ten team rows."* A
 * `team_count 10 × team_size 1` room is a **free-for-all** (A132 §1 row 3) and
 * would otherwise render here as **ten blocks of one** — ten `TeamBlock` beds
 * with a `VsRule` nowhere and one seat in each. One block is the FFA layout the
 * room already had before `team_count` was ever used as a stand-in for *"is
 * this a team game"*.
 *
 * **What does NOT change:** every shape that has sides keeps exactly the
 * blocks it had — `2 × 1` two, `2 × 2` two, `4 × 1` four, `4 × 2` four — and
 * `1 × N` already produced one block through `Math.max(1, …)`. The predicate
 * moves; no reachable layout does.
 */
export function seatBlocks(state: GameRoomStateView): GameSeatView[][] {
  const teamCount = roomHasSides(roomShape(state)) ? Math.max(1, state.teamCount) : 1;
  const blocks: GameSeatView[][] = Array.from({ length: teamCount }, () => []);
  state.seats?.forEach((seat) => {
    // Interleaved, matching the server's `teamIndexForSeat`: seat 0 → team 0,
    // seat 1 → team 1, seat 2 → team 0. Derived from the index rather than from
    // `seat.teamIndex` so an **empty** slot still lands in the right block.
    const block = blocks[seat.seatIndex % teamCount];
    block?.push(seat);
  });
  return blocks;
}

/* ==========================================================================
   Scores (§7.4) and standings (§8.1)
   ========================================================================== */

/** Seats that were dealt in — an empty slot has no row. */
function playedSeats(state: GameRoomStateView): GameSeatView[] {
  const seats: GameSeatView[] = [];
  state.seats?.forEach((seat) => {
    if (seat.status !== "empty" && seat.nickname !== "") seats.push(seat);
  });
  return seats;
}

/**
 * The between-rounds table.
 *
 * **LOCKED (§7.4, §19 line 12) — ordered by `seatIndex`, never by score.**
 * Ranking is `Standings`' job at results; re-sorting rows between rounds moves
 * faces under a thumb for no gain, and it turns a five-second breather into a
 * chart. `state.seats` is already `seatIndex`-ordered and this function does not
 * sort it — that is the property, expressed as an absence.
 */
export function scoreRows(state: GameRoomStateView, sessionId: string | null): ScoreRowView[] {
  return playedSeats(state).map((seat) => {
    const status = seatStatus(seat.status);
    const isYou = sessionId !== null && seat.sessionId === sessionId;
    return {
      seatIndex: seat.seatIndex,
      nickname: seat.nickname,
      avatarUrl: seat.avatarUrl,
      colorIndex: seat.colorIndex,
      roundScore: seat.roundScore,
      totalScore: seat.totalScore,
      isYou,
      /** Bots §3.5: the between-rounds table is one of the four chip surfaces. */
      isBot: seat.isBot === true,
      // A dropped or departed row keeps its pill and its scores (§7.4).
      statusLabel:
        seat.status === "reconnecting" || seat.status === "forfeited" || seat.status === "left"
          ? (status?.label ?? null)
          : null,
      statusTone: status?.tone ?? "quiet",
      dimmed: seat.status === "forfeited" || seat.status === "left",
    };
  });
}

/**
 * **The ordinal-word catalogue keys, frozen — `{place}`'s one derivation**
 * (D2 §1c, T-568).
 *
 * `as const` so every member is a **literal** `StringKey` and `t()` takes it
 * with no cast — the same device `SIDES[n].nameKey` uses, and the reason a
 * renamed or deleted catalogue key is a compile error here rather than a
 * runtime miss on a money screen.
 *
 * **Indexed by `place - 1`, and the lookup is total:** an out-of-range place
 * reads `undefined`, which {@link placePhrase} turns into "no name" rather than
 * into a number.
 */
const PLACE_KEYS = [
  "room.standing.place.1",
  "room.standing.place.2",
  "room.standing.place.3",
  "room.standing.place.4",
  "room.standing.place.5",
  "room.standing.place.6",
  "room.standing.place.7",
  "room.standing.place.8",
  "room.standing.place.9",
  "room.standing.place.10",
] as const;

/**
 * A placement, as the **word** a listener hears — `1 → "First"`.
 *
 * ── Why a word (D2 §1c, Ivy's channel argument, restated once) ─────────────
 *
 * The visible numeral is bare and `aria-hidden` because it sits in a place
 * column, in a list sorted by placement, next to a crown. A screen-reader user
 * gets **one sentence** with none of that: *"2: Red, 90 points, won 45 coins."*
 * asks the listener to infer that the leading "2" is a rank and not a second
 * score. The word carries "this is a rank" in itself and needs no table.
 *
 * ── `null` past the table, and why that beats both alternatives ────────────
 *
 * **There is no seat ceiling in this system to size the table against** —
 * `game_modes.max_players` is `team_count * team_size` with both only
 * `CHECK (> 0)`, `room_configs.max_players_override` has no CHECK, and
 * `@wr/config`'s constraint layer caps nothing. Ten covers the largest seeded
 * shape (`2v2`, 4 seats) and the largest the codebase documents (the `10 × 1`
 * royale in {@link standingTeams}' gate table), and past it this answers `null`
 * so the row composes **no accessible name at all**.
 *
 * - **Not a digit or an "11th" fallback** — Nora: *"a spoken '11: Red' is the
 *   exact defect this consult closes."* A fallback re-opens the drift.
 * - **Not a throw** — this runs inside the results screen's render path, and
 *   blanking a money screen over a missing ordinal is a worse failure than an
 *   unlabelled row. An unlabelled row is an **already-shipped shape**: it is
 *   what every team-member row is, and it degrades to reading its own content
 *   (nickname, pill, score).
 * - **Loud, per Theo's fail-loudly ruling**, via `console.error` — the repo's
 *   own idiom for an impossible-shaped input on a render path
 *   (`game/word-search/read-state.ts`, `Room.tsx`). Silence here is what let
 *   the cardinal drift live for a sprint.
 */
function placePhrase(place: number): string | null {
  const key = PLACE_KEYS[place - 1];
  if (key === undefined) {
    console.error(
      `[standings] no ordinal word for place ${place}: room.standing.place.N covers 1…${PLACE_KEYS.length}. ` +
        "This row will carry no accessible name — a spoken cardinal is the defect D2 closed. " +
        "Extend the catalogue (and gate the new words through Nora) rather than adding a fallback.",
    );
    return null;
  }
  return t(key);
}

/**
 * A standings row's accessible name, composed — **the six-key selection, in one
 * place** (D2 + Nora's gate, T-568).
 *
 * Mirrors {@link seatA11yName}'s shape: the key choice lives in a function, not
 * spread across two call sites that can drift. The three axes are independent
 * and are asked in the order their rulings bind:
 *
 * 1. **`isBot`** — its own key (§22.8.2, one key per row shape), and it carries
 *    **no prize clause at all**: under A80 a bot only ever sits at a free
 *    config, so the clause could never say anything (D2's supersession of the
 *    T-268 string).
 * 2. **`prizeCoins > 0`** — the **exact predicate the visible coin chip uses**,
 *    so the spoken and the painted channel cannot drift apart about whether
 *    this player won money. At `0` the clause is **omitted**, not zeroed (Ivy
 *    ruled the zero branch *omit*; `{prize}` never renders "0").
 * 3. **`score === 1`** — the `.one` sibling, the house mechanism this catalogue
 *    has ratified five times. `POINTS_PER_WORD = 1`, so "1 point" is a value
 *    the game reaches constantly, not an edge.
 *
 * **`{prize}` goes through `coinsPhrase()` and nothing else** (Addendum 7 §1a):
 * exactly one place in this codebase turns a coin integer into words, and a
 * second spelling of "coins" in a template is refused. That is also why the
 * spoken amount is now thousands-separated exactly like the chip.
 *
 * **Returns `undefined`, not a partial sentence**, when the place has no word —
 * `ScoreRowView.a11yName` is already optional and an absent `aria-label` is the
 * team-member row's shipped shape. See {@link placePhrase}.
 *
 * **LOCKED — nothing here derives winner status.** Not from `place`, not from
 * `score`, not from row order. `isWinner` is `summarize()`'s answer and the
 * ledger pays from the same object; a client-side recomputation is a second
 * answer to a money question. A zero-prize winner's name therefore says nothing
 * about winning — **pre-existing, ruled, filed, and not this row's to close**
 * (Dex, Addendum 3).
 */
function standingA11yName(input: {
  place: number;
  /** `null` when the row has no identity to speak — see the refusal below. */
  nickname: string | null;
  score: number;
  prizeCoins: number;
  isBot: boolean;
  isYou?: boolean;
  statusLabel?: string | null;
}): string | undefined {
  const place = placePhrase(input.place);
  if (place === null) return undefined;

  const { nickname, score, prizeCoins, isBot } = input;
  /**
   * **No identity, no name** — Nora's ruled shape for the null branch, and the
   * closure of D2's `label ?? ""` watch-item (S15 `M1` rider): *"that row must
   * not compose a name at all rather than compose one with an empty slot."*
   * `"First: , 12 points…"` is a name Ivy would not certify, and an unnamed row
   * is the shipped degradation, not a new one.
   *
   * Reachable through `sideName()` answering `null` for a side the four-row
   * table does not have — see the guarantee note at the team call site, which
   * is enforced three packages away and server-side.
   */
  if (nickname === null) return undefined;
  const isYou = input.isYou ?? false;
  const one = score === 1;

  /**
   * **`bot × you` is REFUSED, loudly** (Theo's deck: *"identity is a single
   * three-way state, never two independent flags; a row is never simultaneously
   * the viewer and a bot"*). It is not a 25th cell and it gets no composed name:
   * a bot row wearing the viewer's marker is a screen telling a listener that
   * the opponent is them. The refusal is loud rather than silent for the same
   * reason `placePhrase`'s is — this is the repo's fail-loudly idiom on a render
   * path, and silence is what let the cardinal drift live for a sprint.
   */
  if (isBot && isYou) {
    console.error(
      "[standings] a row cannot be both the viewer and a bot: isBot && isYou. " +
        "Identity is one three-way state (none | you | bot), not two flags. " +
        "This row will carry no accessible name rather than compose an identity that cannot exist.",
    );
    return undefined;
  }

  /**
   * **`R-D7.1`'s marker, on the IDENTITY** (`R-D7.6`: the marker attaches to the
   * identity, the token to the seat). It rides the `{nickname}` slot as a comma
   * appositive — the same grammatical slot the shipped `bot` fragment fills —
   * so no sentence key multiplies. `bot` stays baked into its own two keys
   * because it shipped that way and its composed outputs are Nora-passed.
   */
  const identity = isYou ? `${nickname}, ${t("room.standing.a11y.you")}` : nickname;

  /**
   * **`R-D7.2`'s token, on the SEAT, terminating its own clause** — Nora's
   * 2026-09-02 REPLACE-WITH, ratified in `design/wr-s16-d5-d6-copy.md`:
   *
   * > *"The separator after `Forfeited` is a period plus one space (`". "`),
   * > never a comma, and never a bare period."*
   *
   * A comma into a bare numeral produces *"Red forfeited zero points"*, which
   * asserts the inverse of the fact; a bare period invites a decimal reading.
   * **The period is composition, not copy, so it lives here and not in the
   * catalogue** — and the word itself is never minted a second time: it arrives
   * as the label the visible `StatusPill` renders, which is the single existing
   * derivation of the fact.
   *
   * The token is a **token, never a sentence** (§8.7 unchanged), and because it
   * terminates the identity/status clause *before* the score clause, `R-D7.6`'s
   * *"never between a money quantity and its unit"* holds **by construction**.
   */
  const status = input.statusLabel ? `${input.statusLabel}. ` : "";

  if (isBot) {
    /**
     * **The `isBot` branch drops the prize clause, and that is now LOUD** (S15
     * `M1` rider, QA-1 `A3-1`). It is correct **only** under A80 / LOCKED rule
     * 32 — a bot sits only at a fee-0 config — and nothing local says so. A bot
     * row that won a prize and silently dropped its `won` clause is a screen
     * dropping a money fact, so the impossible state is reported rather than
     * pinned as if it were desired. **Trigger: the first fee-bearing config that
     * admits a bot.**
     */
    if (prizeCoins > 0) {
      console.error(
        `[standings] bot row at place ${input.place} carries prizeCoins ${prizeCoins}: ` +
          "the bot sentence keys have no `won` clause, so this name will not state the prize. " +
          "That composition is correct only while A80/rule 32 keeps bots at fee-0 configs. " +
          "A fee-bearing config that admits a bot needs a bot+prize sentence shape (Nora-gated) — " +
          "do not let the clause stay dropped.",
      );
    }
    return t(one ? "room.standing.a11y.bot.one" : "room.standing.a11y.bot", {
      place,
      nickname: identity,
      score,
      status,
    });
  }
  if (prizeCoins > 0) {
    return t(one ? "room.standing.a11y.one" : "room.standing.a11y", {
      place,
      nickname: identity,
      score,
      prize: coinsPhrase(prizeCoins),
      status,
    });
  }
  return t(one ? "room.standing.a11y.noPrize.one" : "room.standing.a11y.noPrize", {
    place,
    nickname: identity,
    score,
    status,
  });
}

/**
 * The final table.
 *
 * **LOCKED (§8.2, §19 line 13) — the screen renders the server's winner set and
 * never derives it from the scoreboard.** `summarize()` produces winners and the
 * prize split, and the ledger pays from the same object; a client that
 * re-computed "highest score wins" will eventually disagree with the money, and
 * when it does the player believes the screen and not the wallet.
 *
 * With `null_game` this is immediately load-bearing: it scores everyone `0` and
 * picks the lowest seat index, so a score-derived UI would show a four-way tie
 * next to a single credited player. **A 0–0 game with one winner is correct in
 * S4 and is not a bug.**
 *
 * **Ordering here is by placement**, then `seatIndex` for ties — the one place
 * this screen re-orders, and it re-orders once, on arrival.
 */
export function standingRows(state: GameRoomStateView, sessionId: string | null): ScoreRowView[] {
  const bySeat = new Map(playedSeats(state).map((seat) => [seat.seatIndex, seat]));
  const rows: ScoreRowView[] = [];

  state.results?.standings?.forEach((standing) => {
    const seat = bySeat.get(standing.seatIndex);
    if (!seat) return;
    const isYou = sessionId !== null && seat.sessionId === sessionId;
    const isBot = seat.isBot === true;
    rows.push({
      seatIndex: seat.seatIndex,
      nickname: seat.nickname,
      avatarUrl: seat.avatarUrl,
      colorIndex: seat.colorIndex,
      roundScore: seat.roundScore,
      totalScore: standing.totalScore,
      isYou,
      isBot,
      place: standing.place,
      prizeCoins: standing.prizeCoins,
      isWinner: standing.isWinner,
      /**
       * §8.7 N3: a member credited less than the team total — or nothing — needs
       * **no explanation line**; the existing `FORFEITED` pill on their own row
       * and the team's prize chip sit next to each other and that is the whole
       * story. Carried here so a team-mode member row can render it.
       */
      statusLabel:
        seat.status === "forfeited" || seat.status === "left"
          ? (seatStatus(seat.status)?.label ?? null)
          : null,
      statusTone: seatStatus(seat.status)?.tone ?? "quiet",
      dimmed: seat.status === "forfeited" || seat.status === "left",
      /**
       * **One key per row SHAPE** (§22.8.2), and since T-568 the six-shape
       * selection lives in {@link standingA11yName} rather than at this call
       * site — two call sites choosing keys independently is how the flat and
       * the team table start describing money differently.
       *
       * **Every value handed over is the server's, untouched:** `standing.place`,
       * `standing.totalScore` and `standing.prizeCoins` are read straight off
       * `summarize()`'s answer. The builder formats them; it computes nothing.
       */
      a11yName: standingA11yName({
        place: standing.place,
        nickname: seat.nickname,
        score: standing.totalScore,
        prizeCoins: standing.prizeCoins,
        isBot,
        /**
         * `SM-1`'s two facts (T-642). Both are read off the values this row
         * already carries — `isYou` from the session match above and the status
         * label from the same `seatStatus()` derivation the visible pill uses —
         * so the spoken and the painted channels cannot say different words
         * about the same seat.
         */
        isYou,
        statusLabel:
          seat.status === "forfeited" || seat.status === "left"
            ? (seatStatus(seat.status)?.label ?? null)
            : null,
      }),
    });
  });

  return rows.sort((a, b) => (a.place ?? 0) - (b.place ?? 0) || a.seatIndex - b.seatIndex);
}

/**
 * The **team-mode** final table — Ivy §8.7 N3, LOCKED (T-237 N3).
 *
 * > *"In team modes (`team_size > 1`) `Standings` renders no individual place
 * > column. The place numeral and the crown sit on the **team** row; the prize
 * > chip sits on the **team** row; individual rows carry their nickname and their
 * > score and nothing else."*
 *
 * Everything here is read off the **server's** standings and nothing is
 * re-derived (§8.2, LOCKED): the team's place is its members' place, which the
 * server now guarantees is one value per team; the team is a winner iff any
 * member is; the team's prize is the sum of what its members were actually
 * credited, so the chip and the ledger cannot disagree; the team's score is the
 * sum of its members' totals, which is the number the winner was resolved from.
 *
 * `{place}` is **omitted** from a member's accessible name and carried on the
 * team row — one key, one shape, no clone.
 *
 * Returns `null` at `team_size <= 1`, which is the caller's signal to render the
 * flat table: in 1v1 the place *is* the answer and the column stays.
 *
 * ── The two gates, and why they are two (Quinn, T-512 §6 item 2) ────────────
 *
 * **`teamSize <= 1` is a NAMES gate and `roomHasSides` is a SIDES gate. They
 * agree on the royale by luck and disagree elsewhere, so both are asked, each
 * with its own reason, and neither stands in for the other:**
 *
 * | shape | names gate | sides gate | this table |
 * |---|---|---|---|
 * | `2 × 1` (1v1) | flat — *a team of one is a player* | has sides | **flat**, and its seats still wear side colour on the avatar ring (D3a §2.1) |
 * | `4 × 1` (1v1v1v1) | flat — same rule | has sides | **flat**, same |
 * | `2 × 2` (2v2) | named | has sides | **team rows**, bedded and named |
 * | `10 × 1` (royale) | flat | **no** sides | **flat** — A132 §1 row 3 |
 * | `1 × 4` (one camp) | named | **no** sides | **flat** — A132's *"a room with no sides renders the flat standings"*; there is no side to name or bed |
 *
 * The last row is the only reachable behaviour change: before R2 a `1 × 4`
 * room produced **one** team block labelled "Team A" summing everybody, which
 * is a subtotal band over the whole table. No seeded mode is `1 × N` (all three
 * are `team_count 2`) and there is no operator write path for `team_count`, so
 * nothing in either environment renders it today.
 */
export function standingTeams(
  state: GameRoomStateView,
  sessionId: string | null,
): StandingTeamView[] | null {
  // Gate 1 — NAMES. LOCKED (§5): a team of one is a player, so 1v1 and
  // 1v1v1v1 get the flat table with its place column, which is the answer there.
  if (state.teamSize <= 1) return null;
  // Gate 2 — SIDES (A132). No sides ⇒ no camp worth a row of its own.
  if (!roomHasSides(roomShape(state))) return null;

  const teamCount = Math.max(1, state.teamCount);
  const rows = standingRows(state, sessionId);
  const blocks = new Map<number, ScoreRowView[]>();
  for (const row of rows) {
    const teamIndex = row.seatIndex % teamCount;
    const block = blocks.get(teamIndex) ?? [];
    block.push(row);
    blocks.set(teamIndex, block);
  }

  const teams: StandingTeamView[] = [];
  for (const [teamIndex, members] of blocks) {
    const ordered = [...members].sort((a, b) => a.seatIndex - b.seatIndex);
    const place = ordered[0]?.place ?? 1;
    const totalScore = ordered.reduce((sum, row) => sum + row.totalScore, 0);
    const prizeCoins = ordered.reduce((sum, row) => sum + (row.prizeCoins ?? 0), 0);
    const isWinner = ordered.some((row) => row.isWinner === true);

    /**
     * **The side name — one lookup, used for the visible label AND for the
     * accessible name's `{nickname}` slot**, so the two cannot disagree. This
     * is D1 Decision 1's repoint at `StandingTeamView.label` (`Room.tsx`
     * :745–757 in D1's own citation) and, through it, at the standings team
     * row's `room.standing.a11y`.
     *
     * ── The guarantee, corrected — S15 `M1` rider (T-568 + QA-1, independently) ─
     *
     * This comment used to read *"gate 2 above established the room has sides,
     * so `teamCount` is `2 … 4`"*. **That is false.** `roomHasSides()` is
     * `teamCount >= MIN_SIDES && (teamCount <= MAX_SIDES || teamSize > 1)` — its
     * last clause **admits `teamCount > 4` whenever `teamSize > 1`**, which is
     * exactly the shape this loop runs on. Gate 2 therefore establishes
     * *"has sides"* and **not** a ceiling, and a five-side room would reach here
     * with `sideName()` answering `null` for the fifth team.
     *
     * **The real guarantee is `assertSupportedSideCount`, and its distance is
     * the point:** it lives in `@wr/contracts` (`sides.ts`), it is enforced
     * **server-side in `apps/game`** (`BaseGameRoom.ts`, once, at spawn), and it
     * refuses exactly `team_count > 4 AND team_size > 1` *"at spawn with a typed
     * error, not degraded at render time"*. So the invariant holding here is
     * **three packages and one process boundary away** from the code that
     * depends on it, and no local expression enforces it. That distance is
     * why the branch below is real rather than defensive.
     */
    const label = sideName(teamIndex, roomShape(state));
    /**
     * **The side bed's provenance** (amendment 3.4 / D3a §7.3): the bed is
     * painted from `SIDES[sideIndex].colorIndex` — the same single value the
     * ring, the monogram bed and the board claim already read — and never from
     * a second computation. `null` here is *"this row carries no side bed"*,
     * which is the exact predicate D3a §2.1 scopes the `--wr-good` demotion by.
     *
     * **S12 R3 routed this through {@link sideColour}** rather than leaving the
     * lookup spelled out here. R3 beds two more surfaces from the same fact, and
     * three inline copies of `SIDES[sideIndexForTeam(…)]?.colorIndex ?? null` is
     * three places for the sides gate to be re-spelled slightly differently —
     * the failure mode `roomShape()` exists to prevent, in the next field along.
     * Behaviour is unchanged: `sideColour` is that expression, with the table
     * lookup delegated to `@wr/contracts`' own total accessor.
     */
    const colorIndex = sideColour(teamIndex, roomShape(state));

    teams.push({
      teamIndex,
      colorIndex,
      label,
      place,
      totalScore,
      prizeCoins,
      isWinner,
      /**
       * The team row takes the same builder as the flat row (T-568), so the two
       * tables cannot describe a prize differently. `isBot: false` is a fact
       * about the shape, not a default: a **team** is never a bot — the bot
       * chip lives on the member rows inside this block, which carry no
       * accessible name at all (§8.7).
       *
       * **`label ?? ""` is GONE — D2's watch-item, closed** (S15 `M1` rider).
       * Nora's ruled shape for the null branch, verbatim: *"that row must not
       * compose a name at all rather than compose one with an empty slot."*
       * An empty identity slot composes `"First: , 12 points, won 90 coins."`,
       * which Ivy would not certify (Addendum A A.4 item 1) — and an unnamed row
       * is an **already-shipped shape**: it is what every team-member row is,
       * and it degrades to reading its own content. So a nameless side yields
       * `undefined` here and the row carries no `aria-label`.
       *
       * **`R-D7.7` and `R-D7.8`, the negative half, are enforced by omission
       * and stated so nobody "completes" the call:** this call passes **no
       * `isYou` and no `statusLabel`**. In team modes the `you` marker and the
       * `Forfeited` token belong to the MEMBER row, never the team row — *"a
       * team row's identity is a **side**, and a side is not the viewer"*, and
       * *"a team has no seat and no status"*. Adding either here reverses a
       * ruling.
       */
      a11yName: standingA11yName({
        place,
        nickname: label,
        score: totalScore,
        prizeCoins,
        isBot: false,
      }),
      /**
       * The member rows, stripped of everything the team row now carries —
       * including the accessible name.
       *
       * **Ivy §8.7: *"`room.standing.a11y`'s `{place}` param is omitted on
       * individual rows in team modes and carried on the team row. One key, one
       * shape, no clone."*** A parameterised sentence cannot have a parameter
       * removed, and cloning the key is what she bars — so the member row carries
       * **no** `aria-label` and is read as its own content: the nickname, the
       * pill, and the score, all of which are rendered text. Nothing is lost,
       * because §13 item 4's reason for the string in the first place was *"so a
       * screen-reader user gets the prize without seeing the chip"*, and in a team
       * mode a member row has no chip. Recorded as a reading of the rule rather
       * than a new COPY-SLOT invented by a developer (T-238 Output).
       */
      rows: ordered.map(({ a11yName: _place, ...row }) => row),
    });
  }

  /**
   * §8.7: team ordering is by placement, then `teamIndex` for ties.
   *
   * **The tiebreak is now the index itself, and that is a repair the copy
   * change forced.** It was `label.localeCompare(label, { numeric: true })` —
   * which sorted `"Team A" … "Team D"` into `teamIndex` order *because the
   * letter was derived from the index*. Side names are words: `localeCompare`
   * would order Blue, Green, Purple, Red — sides `0, 2, 3, 1` — and §8.7's
   * rule says `teamIndex`, not "alphabetically". Sorting on the number says
   * what the rule says and is byte-identical to the shipped order on every
   * reachable shape (A…D ↔ 0…3).
   */
  return teams.sort((a, b) => a.place - b.place || a.teamIndex - b.teamIndex);
}

/**
 * **The between-rounds side subtotal band** — §7.4's LOCKED anatomy row
 * (*"a team subtotal row in `display-sm` on `--wr-ink-06`"*), wired at last
 * (S12 R2b, Ivy's R2-flags ruling §1.3). It is `Scoreboard`'s `teams` prop and
 * nothing else renders it.
 *
 * `null` is *"this screen shows no band"*, which is the caller's whole decision.
 *
 * ── The predicate is NOT re-derived here, and that is the point ─────────────
 *
 * Ivy §1.3, binding: *"the band renders **exactly where `standingTeams` returns
 * non-null** — the same two gates, in the same order, from the same helper"*.
 * So this asks {@link standingTeams} **itself** rather than restating
 * `teamSize > 1 && roomHasSides(...)`, and `standingTeams` is left byte-identical
 * (the ticket's own words: *"the helper is the authority — you consume it"*).
 * Two surfaces, one fact, and no copy of the gates that could drift out of step
 * with it:
 *
 * | shape | `standingTeams` | this band |
 * |---|---|---|
 * | `2 × 2` (2v2) | team rows | **two bands** |
 * | `2 × 1` (1v1) | `null` — *a team of one is a player* | **none** |
 * | `4 × 1` (1v1v1v1) | `null` — same NAMES gate, though it **has** sides | **none** |
 * | `1 × 4` (one camp) | `null` — no sides (A132) | **none** |
 * | `10 × 1` (royale) | `null` — both gates fail | **none** |
 *
 * The `4 × 1` row is Quinn's T-512 item-2 trap, now live on a second surface: a
 * "subtotal" of one player is that player's own row printed twice.
 *
 * The viewer is irrelevant to the gate, so `null` is passed as the session — a
 * band carries no `You` marker and no per-seat state; `standingTeams`' second
 * argument only decides `isYou` on rows this function discards.
 *
 * ── What the band shows, and where each value comes from ────────────────────
 *
 * **`total` is the side's live total, summed from {@link scoreRows}** — the same
 * rows rendered above it, so the band cannot disagree with the column it sits
 * under. It is emphatically *not* `standingTeams`' total: that one is read off
 * `state.results.standings`, which is empty between rounds. **No round score**
 * (§7.4 gives the band one number, and the per-side round score was in the strip
 * five seconds earlier).
 *
 * **Order is `seatIndex` of each side's lowest seat**, which is `ScoreboardProps.teams`'
 * documented contract: `scoreRows` is `seatIndex`-ordered (LOCKED §7.4) and a
 * `Map` preserves first-insertion order, so the order is the seats' and is never
 * sorted here.
 *
 * **`label`** is {@link sideName}, the one lookup the waiting room, the strip and
 * the results row already use. **`colorIndex`'s provenance (amendment 3.4 /
 * D3a §7.2)** is {@link sideColour} — `SIDES[sideIndex].colorIndex` from
 * `@wr/contracts`, the identical value every member's avatar ring on that side
 * already paints from. **S12 R3 made that a shared call rather than a
 * hand-copied expression**, which is what actually stops the four beds becoming
 * four colours; before R3 this file spelled it out twice and the comment
 * *"spelled exactly as `standingTeams` spells it"* was the only thing holding
 * them together.
 */
export function scoreTeams(state: GameRoomStateView): ScoreTeamView[] | null {
  if (standingTeams(state, null) === null) return null;

  const shape = roomShape(state);
  const teamCount = Math.max(1, state.teamCount);
  const totals = new Map<number, number>();
  for (const row of scoreRows(state, null)) {
    const teamIndex = row.seatIndex % teamCount;
    totals.set(teamIndex, (totals.get(teamIndex) ?? 0) + row.totalScore);
  }

  const teams: ScoreTeamView[] = [];
  for (const [teamIndex, total] of totals) {
    teams.push({
      label: sideName(teamIndex, shape),
      total,
      colorIndex: sideColour(teamIndex, shape),
    });
  }
  return teams;
}

/** The winner set, from the server's `isWinner` flags and nothing else (§8.2). */
export function winnerSeats(state: GameRoomStateView): number[] {
  const winners: number[] = [];
  state.results?.standings?.forEach((standing) => {
    if (standing.isWinner) winners.push(standing.seatIndex);
  });
  return winners;
}

/**
 * **How many teams tied at the top — the `drawnTeams > 1` predicate Theo's
 * 2026-08-02 addendum names as the tie headline's condition.**
 *
 * The server calls this `d = |W|` (`settlementSplit`): every team tied at the top
 * total with at least one occupied seat. It is **not on the wire**, so it is
 * recomputed here from the two facts that are — the server's `isWinner` flags
 * (§8.2, LOCKED: the winner set is the server's and the client never derives it
 * from scores) and the same `seatIndex % teamCount` team mapping `seatBlocks`
 * and `standingTeams` already use, which mirrors the server's
 * `teamIndexForSeat`. No score is compared here and no tiebreak is invented; the
 * only thing being counted is how many distinct teams the server crowned.
 *
 * ── Why NOT `winners.length`, which is what `prizeSplit`'s `{n}` uses ────────
 *
 * Theo's addendum describes the condition as *"the same flag that already gates
 * `prizeSplit`'s `{n}`"*, and on the launch matrix's 1v1 tables the two are
 * numerically identical — which is exactly what makes the shortcut dangerous.
 * `prizeSplit`'s `{n}` is the count of **winning seats**; this is the count of
 * **winning teams**. In a 2v2 they diverge in the worst direction: a *decisive*
 * 2v2 win crowns two seats on one team, so `winners.length === 2` would print
 * "It's a tie." over a game somebody won outright. The addendum's binding
 * sentence is the condition (`drawnTeams > 1`), so the condition is what ships.
 *
 * `prizeSplit` is **unchanged** (Theo, explicitly): its `{n}` still counts the
 * seats the prize divided between, which is what "split N ways" means to the
 * player reading it.
 */
export function drawnTeamCount(state: GameRoomStateView): number {
  /**
   * **A132 sweep row 7, per-site verdict: NO-OP, and deliberately so.** This is
   * a `% teamCount` derivation that R2 met and did **not** repoint at
   * `roomHasSides`. It does not ask *"is this a team game"*; it counts how many
   * distinct camps the server crowned, and A132 §1 keeps teams as data in every
   * shape (*"in a `team_count 10 × team_size 1` room `teamIndexForSeat` still
   * answers `seatIndex % 10` — teams remain data"*). In a royale each camp is
   * one player, so two tied winners are two drawn camps and the tie headline is
   * right. Putting the sides predicate here would collapse a ten-way FFA to one
   * camp and print *"{name} won."* over a draw.
   */
  const winners = winnerSeats(state);
  if (winners.length === 0) return 0;
  const teamCount = Math.max(1, state.teamCount);
  return new Set(winners.map((seatIndex) => seatIndex % teamCount)).size;
}

/** The viewer's credited amount — the `room.prize` transaction's size (§8.3). */
export function yourPrizeCoins(state: GameRoomStateView, sessionId: string | null): number {
  const seat = mySeat(state, sessionId);
  if (!seat) return 0;
  let prize = 0;
  state.results?.standings?.forEach((standing) => {
    if (standing.seatIndex === seat.seatIndex) prize = standing.prizeCoins;
  });
  return prize;
}

/**
 * **`me.prizeCoins` from live room state** — the credited `room.prize` amount, or
 * `null` when there is no prize transaction (§17 note 9, A45 addendum).
 *
 * The wire carries `-1` for `null` because a Colyseus number is not nullable
 * (`GameSeatView.prizeCoins`), and **this is the only place that sentinel is
 * read**: everything downstream sees `number | null` and can therefore obey
 * §8.3's rule that `null` is never rendered as `0`. `0` is a different, settled
 * statement — *this room credited you nothing*.
 */
export function myPrizeCoins(
  state: GameRoomStateView | null,
  sessionId: string | null,
): number | null {
  const seat = mySeat(state, sessionId);
  if (!seat) return null;
  return seat.prizeCoins < 0 ? null : seat.prizeCoins;
}

/** Did the viewer win? From the server's set — never from a score comparison. */
export function viewerWon(state: GameRoomStateView, sessionId: string | null): boolean {
  const seat = mySeat(state, sessionId);
  if (!seat) return false;
  return winnerSeats(state).includes(seat.seatIndex);
}

/**
 * The winner's name for `room.results.otherWon` and `room.results.prizeTo` — a
 * nickname, or the **side name** when the winning set spans a team (§8.2).
 *
 * **D1 Decision 1's fourth render site.** The branch condition is untouched
 * (`winners.length > 1 && teamSize > 1` — the names gate, already correct); the
 * string it resolves moves from `room.team.name` to the side's own name, so the
 * headline reads *"Red won."* rather than *"Team B won."*.
 *
 * `sideName` is total, and when it answers `null` the function **falls through
 * to the nickname branch** rather than printing nothing. That is only
 * reachable in a room with no sides and `teamSize > 1` (a `1 × N` single camp),
 * where naming the first credited player is the honest answer: there is no side
 * for the sentence to be about.
 */
export function winnerName(state: GameRoomStateView): string {
  const winners = winnerSeats(state);
  if (winners.length === 0) return "";
  const teamCount = Math.max(1, state.teamCount);
  if (winners.length > 1 && state.teamSize > 1) {
    const firstSeat = winners[0] ?? 0;
    const side = sideName(firstSeat % teamCount, roomShape(state));
    if (side !== null) return side;
  }
  let name = "";
  state.seats?.forEach((seat) => {
    if (seat.seatIndex === winners[0]) name = seat.nickname;
  });
  return name;
}

/* ==========================================================================
   Money copy (§4.2, §8.3)
   ========================================================================== */

/**
 * A coin amount, as a phrase for interpolation into copy.
 *
 * `lobby.coins.amount` is the existing, ratified fragment and it is reused
 * rather than cloned — §16's LOCKED "one string per fact" applies to fragments
 * too, and a second "N coins" is how the room and the lobby end up describing
 * money differently.
 */
export function coinsPhrase(coins: number): string {
  return t("lobby.coins.amount", { n: coins.toLocaleString() });
}

/**
 * The money plate's two halves, from **snapshots** (§4.2, §18 — LOCKED).
 *
 * Both take the narrowest shape that answers, rather than a whole room state, so
 * the same functions serve the live snapshot and the frame a refused rejoin
 * carries (A42). The numbers are the room's snapshot in either case, which is
 * what makes the two frames incapable of disagreeing.
 */
export function entryMoney(state: { entryFeeCoins: number }) {
  return {
    eyebrow: t("lobby.money.entry"),
    // `null` renders the WORD, not "unknown": the number is known and it is zero,
    // which is exactly why it must not be printed (§5.2, LOCKED).
    coins: state.entryFeeCoins === 0 ? null : state.entryFeeCoins,
    word: t("lobby.entry.free"),
  };
}

export function prizeMoney(state: { prizeCoins: number }) {
  return {
    eyebrow: t("lobby.money.prize"),
    coins: state.prizeCoins === 0 ? null : state.prizeCoins,
    word: t("lobby.prize.none"),
  };
}

/* ==========================================================================
   The phase chip (§11.1)
   ========================================================================== */

export function phaseChip(
  state: GameRoomStateView,
  screen: RoomScreenState,
): { label: string; tone: "ink" | "coin" | "good" } | null {
  switch (screen) {
    case "waiting":
      return state.phase === "ready_check"
        ? { label: t("room.phase.readyCheck"), tone: "coin" }
        : { label: t("room.phase.filling"), tone: "ink" };
    case "round":
    case "between":
      return { label: t("room.phase.round", { n: state.roundIndex }), tone: "ink" };
    case "results":
      return { label: t("room.phase.final"), tone: "good" };
    default:
      /**
       * **§9.7 LOCKED — T7 gets no chip word of its own.** T1 and T7 are the
       * same server phase, and §11.1's rule is one word per fact; a chip cannot
       * carry a cause anyway, and minting a second `cancelled` word is how the
       * header and the panel end up naming the same phase twice. **Quinn: an
       * identical chip on T1 and T7 is correct, not a copy bug** — the
       * difference is carried entirely by the disc colour, the title and the two
       * body lines. §11.1 has no `cancelled` token today, so both render none.
       */
      return null;
  }
}

/* ==========================================================================
   The navigation guard's trigger set (§3.1, §10)
   ========================================================================== */

/**
 * **LOCKED (§10, re-confirmed 2026-07-29 in §9.0) — the leave-confirm dialog's
 * trigger set is `round` + `between_rounds`, and it may NOT be widened to
 * `ready_check`.**
 *
 * Every line of that dialog's copy claims a forfeit — the entry is lost, it
 * counts as a loss — and in `ready_check` the server refunds 100% and records
 * nothing (Juno Ruling #3). A dialog that claims a forfeit which will not happen
 * is the same defect as a panel that does, and it is worse for being the *last*
 * thing the player reads before deciding. **Quinn: tapping `Leave` during
 * `ready_check` must open no dialog.**
 *
 * The same predicate answers "is this navigation guarded?" (§3.1), because they
 * are the same question: money is at risk in exactly these two phases.
 */
export function isMoneyAtRisk(phase: string): boolean {
  return MID_GAME_PHASES.has(phase as RoomPhaseView);
}
